/**
 * Dashboard / Postharvest.
 *
 * Dos widgets con pantalla completa independiente:
 *
 *  1. "Próximos despachos" — grid de 3 días (33% cada uno) con los pedidos
 *     programados. Cada tarjeta de pedido se puede abrir en un modal con
 *     todo el detalle (items, cajas, totales, cliente final, OCs ligadas).
 *     Nav ← / → mueve el ancla ±3 días; "Hoy" resetea.
 *
 *  2. "Inventario actual" — por variedad, listando cada stemLength con el
 *     desglose por etapa (sin procesar / en proceso / cuarto frío). El
 *     usuario pidió poder ver "cuántos Ruscus hay de 60cm vs 30cm".
 *
 * El botón de pantalla completa de cada widget llama a requestFullscreen()
 * sobre el wrapper correspondiente. `useFullscreen` escucha
 * `fullscreenchange` para re-sincronizar si el usuario sale con Esc.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Leaf,
  Maximize2,
  Minimize2,
  Package,
  Snowflake,
  Scissors,
  ShoppingCart,
  Sparkles,
} from 'lucide-react'
import {
  SALE_STATUS_LABELS,
  SALE_STATUS_LEGACY_MAP,
} from '@expogreens/shared'
import { Card, CardContent } from '@/components/ui/Card'
import { PageHeader } from '@/components/ui/PageHeader'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import { useApi } from '@/lib/useApi'
import { apiCall } from '@/lib/api'
import { cn } from '@/lib/utils'

const STAGE_LABEL = {
  unprocessed: 'Sin procesar',
  // Por feedback del cliente, "En proceso" se oculta de la UI del dashboard.
  in_process: 'En proceso',
  cold_room: 'Procesado',
}

const STAGE_ICON = {
  unprocessed: Sparkles,
  in_process: Leaf,
  cold_room: Snowflake,
}

const STAGE_COLOR = {
  unprocessed: 'bg-amber-100 text-amber-800',
  in_process: 'bg-blue-100 text-blue-800',
  cold_room: 'bg-sky-100 text-sky-900',
}

// Periodos del filtro del Dashboard. 'custom' permite from/to.
const PERIOD_OPTIONS = [
  { id: 'today', label: 'Hoy' },
  { id: 'week', label: 'Semana (Lun–Dom)' },
  { id: 'month', label: 'Mes' },
  { id: 'custom', label: 'Personalizado' },
]

// Color de chip por status del pedido — usado en la tarjeta de venta.
const SALE_STATUS_COLOR = {
  confirmed: 'bg-gray-100 text-gray-700',
  supplying: 'bg-amber-100 text-amber-800',
  processing: 'bg-blue-100 text-blue-800',
  dispatched: 'bg-emerald-100 text-emerald-800',
  delivered: 'bg-emerald-200 text-emerald-900',
  cancelled: 'bg-red-100 text-red-800',
}

/** Status efectivo del pedido (mapeando los valores legacy del seed v1). */
function effectiveStatus(sale) {
  const raw = sale?.status || 'confirmed'
  return SALE_STATUS_LEGACY_MAP[raw] || raw
}

const DAY_NAMES = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado']
const MONTH_NAMES_SHORT = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']

// Nº de días visibles en el widget de despachos. Lo mantenemos en 3 para
// que cada tarjeta tenga ~33% del ancho; si el usuario quiere más, avanza
// con las flechas.
const STRIP_DAYS = 3

function startOfDay(d) {
  const copy = new Date(d)
  copy.setHours(0, 0, 0, 0)
  return copy
}

function addDays(d, n) {
  const copy = new Date(d)
  copy.setDate(copy.getDate() + n)
  return copy
}

function sameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

function ymd(d) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/** Hook que controla el Fullscreen API sobre un elemento. */
function useFullscreen() {
  const ref = useRef(null)
  const [isFs, setIsFs] = useState(false)
  useEffect(() => {
    const onChange = () =>
      setIsFs(!!document.fullscreenElement && document.fullscreenElement === ref.current)
    document.addEventListener('fullscreenchange', onChange)
    return () => document.removeEventListener('fullscreenchange', onChange)
  }, [])
  const toggle = async () => {
    try {
      if (document.fullscreenElement === ref.current) {
        await document.exitFullscreen?.()
        return
      }
      if (document.fullscreenElement) {
        await document.exitFullscreen?.()
      }
      await ref.current?.requestFullscreen?.()
    } catch {
      /* silencioso — algunos navegadores bloquean sin gesto */
    }
  }
  return { ref, isFs, toggle }
}

function Kpi({ icon: Icon, label, value, tone = 'primary' }) {
  const toneClasses = {
    primary: 'bg-expo-primary',
    olive: 'bg-expo-olive',
    dark: 'bg-expo-dark',
    yellow: 'bg-expo-yellow text-expo-dark',
  }
  return (
    <Card>
      <CardContent className="flex items-center gap-4">
        <div className={`${toneClasses[tone]} text-white p-3 rounded-xl`}>
          <Icon size={28} />
        </div>
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <p className="text-2xl font-bold text-expo-dark">{value}</p>
        </div>
      </CardContent>
    </Card>
  )
}

/**
 * Resume una venta en "líneas de caja" y "sueltos".
 * Usa sale.boxes[] si hay, si no cae a sale.items[].
 */
function summarizeSale(sale) {
  const boxLines = []
  for (const b of sale.boxes || []) {
    const count = Number(b.boxCount) || 0
    if (count <= 0) continue
    const items = (b.items || [])
      .filter((it) => (Number(it.quantity) || 0) > 0)
      .map((it) => ({
        variety: it.variety,
        quantity: Number(it.quantity) || 0,
        stemLength: it.stemLength,
      }))
    if (items.length === 0) continue
    boxLines.push({
      boxCount: count,
      boxSize: b.boxSize,
      stemsPerBox: Number(b.stemsPerBox) || 0,
      items,
    })
  }
  const looseLines =
    boxLines.length === 0
      ? (sale.items || [])
          .filter((it) => (Number(it.quantity) || 0) > 0)
          .map((it) => ({
            variety: it.variety,
            quantity: Number(it.quantity) || 0,
            stemLength: it.stemLength,
            priceUSD: Number(it.priceUSD) || 0,
          }))
      : []
  return { boxLines, looseLines }
}

/** "20 Cajas, cada caja con 30 Ruscus de 50cm, 10 Ruscus de 20cm" */
function BoxLine({ line, compact = false }) {
  const detail = line.items
    .map((it) => `${it.quantity} ${it.variety} de ${it.stemLength}cm`)
    .join(', ')
  return (
    <div className={cn('text-expo-dark', compact ? 'text-sm' : 'text-base')}>
      <span className="font-semibold">{line.boxCount} Cajas</span>
      <span className="text-gray-600">, cada caja con {detail}</span>
    </div>
  )
}

function LooseLine({ line, compact = false }) {
  return (
    <div className={cn('text-expo-dark', compact ? 'text-sm' : 'text-base')}>
      <span className="font-semibold">{line.quantity}</span>
      <span className="text-gray-600">
        {' '}
        {line.variety} de {line.stemLength}cm
      </span>
    </div>
  )
}

/**
 * Resuelve la etiqueta de cliente a mostrar, priorizando siempre al cliente
 * FINAL (al que se envía realmente) y entre paréntesis al mayorista si
 * existe. Dos fuentes posibles de jerarquía, en este orden:
 *
 *   1. El modelo nuevo: `sale.clientId` apunta a un cliente que a su vez
 *      tiene `parentClientId`. En ese caso, el final = ese mismo cliente,
 *      el mayorista = clientsById[parentClientId].
 *   2. El modelo legacy (seed): `sale.finalClientName` embebido. En ese caso
 *      final = finalClientName y el mayorista = sale.clientName.
 */
function resolveClientLabels(sale, clientsById) {
  const primary = clientsById?.get?.(sale.clientId)
  const parent = primary?.parentClientId
    ? clientsById?.get?.(primary.parentClientId)
    : null

  if (parent) {
    return {
      final: primary?.name || sale.clientName || sale.clientId,
      wholesale: parent.name,
    }
  }
  if (sale.finalClientName) {
    return {
      final: sale.finalClientName,
      wholesale: sale.clientName || sale.clientId,
    }
  }
  return {
    final: primary?.name || sale.clientName || sale.clientId,
    wholesale: null,
  }
}

/**
 * Aplana items+cajas a líneas compactas "1000 Ruscus 60cm". Las cajas se
 * multiplican por boxCount para reflejar tallos totales, no por caja.
 */
function flattenSaleLines(sale) {
  const lines = []
  for (const b of sale.boxes || []) {
    const count = Number(b.boxCount) || 0
    if (count <= 0) continue
    for (const it of b.items || []) {
      const q = (Number(it.quantity) || 0) * count
      if (q <= 0) continue
      lines.push({ variety: it.variety, quantity: q, stemLength: it.stemLength })
    }
  }
  if (lines.length === 0) {
    for (const it of sale.items || []) {
      const q = Number(it.quantity) || 0
      if (q <= 0) continue
      lines.push({ variety: it.variety, quantity: q, stemLength: it.stemLength })
    }
  }
  return lines
}

function SaleCard({ sale, onOpen, clientsById }) {
  const labels = resolveClientLabels(sale, clientsById)
  const lines = flattenSaleLines(sale)
  const totalStems =
    Number(sale.totalStems) || lines.reduce((a, l) => a + l.quantity, 0)
  const effStatus = effectiveStatus(sale)

  return (
    <button
      type="button"
      onClick={() => onOpen?.(sale)}
      className="text-left w-full rounded-xl border border-gray-200 bg-white p-3 shadow-sm hover:border-expo-primary hover:shadow-md hover:bg-expo-light/40 active:scale-[0.99] transition-all cursor-pointer"
      title="Click para ver el detalle completo"
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xs font-semibold text-expo-primary">{sale.id}</span>
        <span
          className={cn(
            'text-[10px] px-1.5 py-0.5 rounded-full font-medium',
            SALE_STATUS_COLOR[effStatus] || 'bg-gray-100 text-gray-700',
          )}
        >
          {SALE_STATUS_LABELS[effStatus] || effStatus}
        </span>
        <span className="text-[11px] text-gray-500">{sale.dispatchTime || ''}</span>
      </div>
      <div className="text-sm font-semibold text-expo-dark truncate" title={labels.final}>
        {labels.final}
      </div>
      {labels.wholesale && (
        <div className="text-[11px] text-gray-500 truncate" title={labels.wholesale}>
          (mayorista: {labels.wholesale})
        </div>
      )}
      {lines.length > 0 ? (
        <ul className="mt-1.5 space-y-0.5">
          {lines.map((l, i) => (
            <li key={i} className="text-xs text-expo-dark truncate">
              <span className="font-semibold">
                {l.quantity.toLocaleString('es-CO')}
              </span>{' '}
              <span className="text-gray-700">{l.variety}</span>
              {l.stemLength ? (
                <span className="text-gray-500"> · {l.stemLength}cm</span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <div className="mt-1 text-xs text-gray-400 italic">Sin items</div>
      )}
      <div className="mt-1.5 text-[11px] text-gray-500 flex items-center justify-between">
        <span>{totalStems.toLocaleString('es-CO')} tallos</span>
        <span className="text-expo-primary font-medium">ver detalle →</span>
      </div>
    </button>
  )
}

function DayCard({
  date,
  sales,
  isToday,
  isWeekend,
  onOpenSale,
  fullscreen,
  clientsById,
}) {
  const totalStems = sales.reduce((acc, s) => acc + (Number(s.totalStems) || 0), 0)
  return (
    <div
      className={cn(
        'rounded-2xl border shadow-sm bg-white flex flex-col',
        isToday ? 'border-expo-primary ring-2 ring-expo-primary/30' : 'border-gray-200',
        isWeekend && 'bg-expo-light/40',
      )}
    >
      <div
        className={cn(
          'px-4 py-3 rounded-t-2xl',
          isToday ? 'bg-expo-primary text-white' : 'bg-expo-dark text-white',
        )}
      >
        <div className="text-xs uppercase tracking-wide opacity-80">
          {DAY_NAMES[date.getDay()]}
        </div>
        <div className={cn('font-bold', fullscreen ? 'text-2xl' : 'text-lg')}>
          {date.getDate()} {MONTH_NAMES_SHORT[date.getMonth()]}
        </div>
        <div className="text-[11px] opacity-80">
          {sales.length} pedido{sales.length === 1 ? '' : 's'} ·{' '}
          {totalStems.toLocaleString('es-CO')} tallos
        </div>
      </div>
      {/* Alto duplicado (~960px en modo normal): permite ver cómodamente al
          menos 3 pedidos sin hacer scroll interno. En fullscreen sigue
          ocupando el flex-1 disponible. */}
      <div
        className={cn(
          'p-3 space-y-3 overflow-y-auto',
          fullscreen ? 'flex-1' : 'max-h-[960px] min-h-[640px]',
        )}
      >
        {sales.length === 0 && (
          <div className="text-xs text-gray-400 italic py-6 text-center">
            Sin pedidos programados
          </div>
        )}
        {sales.map((sale) => (
          <SaleCard
            key={sale.id}
            sale={sale}
            onOpen={onOpenSale}
            clientsById={clientsById}
          />
        ))}
      </div>
    </div>
  )
}

/**
 * Acciones manuales de status disponibles desde el detalle del pedido.
 * Solo se exponen las transiciones que el usuario puede disparar a mano:
 * Despachado, Entregado, Cancelar.
 *
 * - "Despachado" pega contra POST /sales/:id/status. Si el backend
 *   responde inventory_not_processed, se muestra "Gestionar inventario"
 *   con el detalle de lo que falta.
 * - "Entregado" no requiere validación extra.
 * - "Cancelar" pide motivo opcional.
 */
function SaleStatusActions({ sale, onStatusChanged }) {
  const [busy, setBusy] = useState(null)
  const [error, setError] = useState(null)

  const eff = effectiveStatus(sale)
  const canDispatch = ['confirmed', 'supplying', 'processing'].includes(eff)
  const canDeliver = eff === 'dispatched'
  const canCancel = !['delivered', 'cancelled', 'dispatched'].includes(eff)

  async function transition(targetStatus, note) {
    setBusy(targetStatus)
    setError(null)
    try {
      await apiCall(`/sales/${sale.id}/status`, {
        method: 'POST',
        body: { status: targetStatus, note: note || null },
      })
      onStatusChanged?.()
    } catch (err) {
      // El apiCall lanza con err.message = JSON serializado. Intento parsear
      // para mostrar el mensaje del backend (ej. inventory_not_processed).
      let msg = err?.message || 'Error al cambiar el estado'
      try {
        const parsed = JSON.parse(msg)
        if (parsed?.error === 'inventory_not_processed') {
          const miss = (parsed.missing || [])
            .map(
              (m) =>
                `${m.variety}${m.stemLength ? ` ${m.stemLength}cm` : ''}: faltan ${(m.needed - m.available).toLocaleString('es-CO')} (hay ${m.available}, requiere ${m.needed})`,
            )
            .join(' · ')
          msg = `Gestionar inventario — ${miss}`
        } else if (parsed?.error) {
          msg = parsed.error
        }
      } catch {
        /* no era JSON */
      }
      setError(String(msg))
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="rounded-xl border border-gray-200 bg-expo-light/30 p-3">
      <div className="text-sm font-semibold text-expo-dark mb-2">
        Cambiar estado del pedido
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant={canDispatch ? 'primary' : 'secondary'}
          onClick={() => transition('dispatched')}
          disabled={!canDispatch || !!busy}
        >
          {busy === 'dispatched' ? 'Despachando…' : 'Marcar como Despachado'}
        </Button>
        <Button
          type="button"
          variant={canDeliver ? 'primary' : 'secondary'}
          onClick={() => transition('delivered')}
          disabled={!canDeliver || !!busy}
        >
          {busy === 'delivered' ? 'Marcando…' : 'Marcar como Entregado'}
        </Button>
        <Button
          type="button"
          variant="secondary"
          onClick={() => {
            const reason = window.prompt('Motivo de la cancelación (opcional):', '')
            // Permite cancelar sin motivo: solo cortar si el usuario hace Esc.
            if (reason === null) return
            transition('cancelled', reason)
          }}
          disabled={!canCancel || !!busy}
        >
          {busy === 'cancelled' ? 'Cancelando…' : 'Cancelar pedido'}
        </Button>
      </div>
      {error && (
        <div className="mt-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
          {error}
        </div>
      )}
      <p className="mt-2 text-[11px] text-gray-500">
        Confirmado, En abastecimiento y En procesamiento se gestionan
        automáticamente (al crear venta, al crear OC ligada y al recibir
        producto). Despachado / Entregado son manuales.
      </p>
    </div>
  )
}

function SaleDetailModal({ sale, onClose, clientsById, onStatusChanged }) {
  if (!sale) return null
  const labels = resolveClientLabels(sale, clientsById)
  const { boxLines, looseLines } = summarizeSale(sale)
  const effStatus = effectiveStatus(sale)
  const totalStems =
    Number(sale.totalStems) ||
    boxLines.reduce(
      (a, b) => a + b.items.reduce((x, it) => x + it.quantity, 0) * (b.boxCount || 1),
      0,
    ) +
      looseLines.reduce((a, it) => a + it.quantity, 0)
  return (
    <Modal
      open={Boolean(sale)}
      onClose={onClose}
      title={`Pedido ${sale.id}`}
      size="xl"
    >
      <div className="space-y-5">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <div className="text-gray-500 text-xs uppercase tracking-wide">
              {labels.wholesale ? 'Cliente final' : 'Cliente'}
            </div>
            <div className="font-semibold text-expo-dark">{labels.final}</div>
            {labels.wholesale && (
              <div className="text-xs text-gray-500">
                mayorista: {labels.wholesale}
              </div>
            )}
          </div>
          <div>
            <div className="text-gray-500 text-xs uppercase tracking-wide">Entrega</div>
            <div className="font-semibold text-expo-dark">
              {sale.deliveryDate || '—'}
            </div>
            {sale.dispatchTime && (
              <div className="text-xs text-gray-500">{sale.dispatchTime}</div>
            )}
          </div>
          <div>
            <div className="text-gray-500 text-xs uppercase tracking-wide">Estado</div>
            <div>
              <span
                className={cn(
                  'inline-block text-xs px-2 py-0.5 rounded-full font-medium',
                  SALE_STATUS_COLOR[effStatus] || 'bg-gray-100 text-gray-700',
                )}
              >
                {SALE_STATUS_LABELS[effStatus] || effStatus}
              </span>
            </div>
            {sale.source && (
              <div className="text-xs text-gray-500 mt-1">origen: {sale.source}</div>
            )}
          </div>
          <div>
            <div className="text-gray-500 text-xs uppercase tracking-wide">Totales</div>
            <div className="font-semibold text-expo-dark">
              {totalStems.toLocaleString('es-CO')} tallos
            </div>
          </div>
        </div>

        <SaleStatusActions sale={sale} onStatusChanged={onStatusChanged} />

        {boxLines.length > 0 && (
          <div>
            <div className="text-sm font-semibold text-expo-dark mb-2">Cajas</div>
            <div className="divide-y divide-gray-100 rounded-xl border border-gray-100">
              {boxLines.map((line, i) => (
                <div key={i} className="p-3">
                  <div className="text-base font-semibold text-expo-dark">
                    {line.boxCount} Cajas {line.boxSize && `(${line.boxSize})`}
                    {line.stemsPerBox
                      ? ` · ${line.stemsPerBox} tallos/caja`
                      : ''}
                  </div>
                  <ul className="mt-1 text-sm text-gray-700 list-disc list-inside">
                    {line.items.map((it, j) => (
                      <li key={j}>
                        <span className="font-medium">{it.quantity}</span>{' '}
                        {it.variety} de {it.stemLength}cm
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}

        {looseLines.length > 0 && (
          <div>
            <div className="text-sm font-semibold text-expo-dark mb-2">
              Tallos sueltos
            </div>
            <div className="overflow-hidden rounded-xl border border-gray-100">
              {/*
               * Por petición del cliente se quitan las columnas con valor
               * monetario (precio USD) del dashboard — es información
               * interna y no debe mostrarse aquí.
               */}
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">Variedad</th>
                    <th className="text-right px-3 py-2 font-medium">Cantidad</th>
                    <th className="text-right px-3 py-2 font-medium">Longitud</th>
                  </tr>
                </thead>
                <tbody>
                  {looseLines.map((it, i) => (
                    <tr key={i} className="border-t border-gray-100">
                      <td className="px-3 py-2">{it.variety}</td>
                      <td className="px-3 py-2 text-right">
                        {it.quantity.toLocaleString('es-CO')}
                      </td>
                      <td className="px-3 py-2 text-right">{it.stemLength}cm</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {sale.linkedPurchaseOrders && sale.linkedPurchaseOrders.length > 0 && (
          <div>
            <div className="text-sm font-semibold text-expo-dark mb-2">
              Órdenes de compra ligadas
            </div>
            <div className="flex flex-wrap gap-2">
              {sale.linkedPurchaseOrders.map((id) => (
                <span
                  key={id}
                  className="text-xs font-mono px-2 py-1 rounded-md bg-expo-light border border-gray-200"
                >
                  {id}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="text-xs text-gray-400">
          Creada {sale.createdAt ? new Date(sale.createdAt).toLocaleString('es-CO') : '—'}
        </div>
      </div>
    </Modal>
  )
}

/**
 * Resuelve el rango de fechas a mostrar a partir del periodo del Dashboard.
 *
 *   today  → solo hoy
 *   week   → Lun-Dom de la semana actual
 *   month  → mes calendario actual
 *   custom → range.from / range.to (inclusive)
 *
 * Devuelve { startDate, endDate, dayCount, anchored } donde `anchored`
 * indica si el rango está fijado por el filtro o si el usuario puede
 * navegar con flechas (caso fallback).
 */
function resolveDispatchRange(period, range) {
  const now = startOfDay(new Date())
  if (period === 'today') {
    return { startDate: now, endDate: now, dayCount: 1, anchored: true }
  }
  if (period === 'week') {
    const day = now.getDay() // 0 dom .. 6 sab
    const offsetToMonday = day === 0 ? -6 : 1 - day
    const monday = addDays(now, offsetToMonday)
    return {
      startDate: monday,
      endDate: addDays(monday, 6),
      dayCount: 7,
      anchored: true,
    }
  }
  if (period === 'month') {
    const start = new Date(now.getFullYear(), now.getMonth(), 1)
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0)
    return {
      startDate: startOfDay(start),
      endDate: startOfDay(end),
      dayCount:
        Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1,
      anchored: true,
    }
  }
  if (period === 'custom' && range?.from && range?.to) {
    const start = startOfDay(new Date(range.from))
    const end = startOfDay(new Date(range.to))
    if (
      !Number.isNaN(start.getTime()) &&
      !Number.isNaN(end.getTime()) &&
      end >= start
    ) {
      return {
        startDate: start,
        endDate: end,
        dayCount:
          Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1,
        anchored: true,
      }
    }
  }
  // Sin filtro válido → comportamiento clásico de 3 días desde hoy con
  // navegación por flechas.
  return { startDate: null, endDate: null, dayCount: 0, anchored: false }
}

/**
 * Props:
 *   sales, onOpenSale, clientsById, period, range
 *
 * `period` y `range` vienen del filtro del Dashboard. Cuando hay un
 * rango anclado por el filtro, el widget muestra ESE rango (sin flechas
 * de navegación). Si no, mantiene el modo clásico de strip navegable.
 *
 * onStatusChanged se acepta pero ya no se usa aquí; el cambio de
 * estado se hace desde el modal de detalle.
 */
function UpcomingDispatches({
  sales,
  onOpenSale,
  clientsById,
  period,
  range,
}) {
  const fs = useFullscreen()
  // Anchor solo se usa cuando el periodo NO fija un rango (fallback).
  const [anchor, setAnchor] = useState(() => startOfDay(new Date()))

  const salesByDate = useMemo(() => {
    const map = new Map()
    for (const s of sales || []) {
      // Match the Sales page's fallback chain so we don't silently drop
      // sales that have saleDate/date but no explicit deliveryDate.
      const key = String(s.deliveryDate || s.saleDate || s.date || '').slice(0, 10)
      if (!key) continue
      if (!map.has(key)) map.set(key, [])
      map.get(key).push(s)
    }
    for (const arr of map.values()) {
      arr.sort((a, b) => (a.dispatchTime || '').localeCompare(b.dispatchTime || ''))
    }
    return map
  }, [sales])

  // Resolver el rango efectivo a partir del filtro del Dashboard.
  const resolved = useMemo(
    () => resolveDispatchRange(period, range),
    [period, range],
  )

  // Lista de días a mostrar. Si el filtro lo fija usamos su startDate/endDate;
  // si no, caemos al strip de 3 días desde anchor.
  const days = useMemo(() => {
    const out = []
    if (resolved.anchored) {
      for (let i = 0; i < resolved.dayCount; i++) {
        out.push(addDays(resolved.startDate, i))
      }
    } else {
      for (let i = 0; i < STRIP_DAYS; i++) out.push(addDays(anchor, i))
    }
    return out
  }, [resolved, anchor])

  const today = startOfDay(new Date())

  const rangeLabel = useMemo(() => {
    if (days.length === 0) return ''
    const first = days[0]
    const last = days[days.length - 1]
    if (sameDay(first, last)) {
      return `${first.getDate()} ${MONTH_NAMES_SHORT[first.getMonth()]} ${first.getFullYear()}`
    }
    return `${first.getDate()} ${MONTH_NAMES_SHORT[first.getMonth()]} – ${last.getDate()} ${MONTH_NAMES_SHORT[last.getMonth()]} ${last.getFullYear()}`
  }, [days])

  // Para periodos largos (mes / custom de muchos días) cambiamos a un
  // layout vertical por día para que no aparezcan tarjetas demasiado
  // estrechas. Umbral: > 7 días → lista; ≤ 7 días → grid.
  const useListLayout = resolved.anchored && days.length > 7

  // Si va en grid, ajustar nº de columnas a la cantidad de días.
  const gridColsClass = useMemo(() => {
    if (useListLayout) return ''
    const n = days.length
    if (n <= 1) return 'grid-cols-1'
    if (n === 2) return 'grid-cols-1 md:grid-cols-2'
    if (n === 3) return 'grid-cols-1 md:grid-cols-3'
    if (n <= 4) return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
    if (n <= 5) return 'grid-cols-1 md:grid-cols-3 lg:grid-cols-5'
    if (n <= 6) return 'grid-cols-1 md:grid-cols-3 lg:grid-cols-6'
    return 'grid-cols-1 md:grid-cols-4 lg:grid-cols-7'
  }, [days.length, useListLayout])

  return (
    <div
      ref={fs.ref}
      className={cn(
        'mb-8 rounded-2xl border border-gray-200 bg-white shadow-sm',
        fs.isFs && 'p-6 h-screen flex flex-col',
      )}
    >
      <div className={cn('p-6', fs.isFs && 'pb-4 flex-shrink-0')}>
        <div className="flex items-center gap-3 flex-wrap mb-4">
          <h3
            className={cn(
              'font-semibold text-expo-dark',
              fs.isFs ? 'text-2xl' : 'text-lg',
            )}
          >
            Próximos despachos
          </h3>
          <span className="text-sm text-gray-500">{rangeLabel}</span>
          <div className="ml-auto flex items-center gap-2">
            {/* Flechas solo en modo fallback (sin filtro anclado).
                Cuando el periodo del Dashboard fija el rango, el usuario
                navega cambiando el periodo arriba, no aquí. */}
            {!resolved.anchored && (
              <>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setAnchor((a) => addDays(a, -STRIP_DAYS))}
                >
                  <ChevronLeft size={16} />
                  <span>Anteriores</span>
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setAnchor(startOfDay(new Date()))}
                >
                  Hoy
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setAnchor((a) => addDays(a, STRIP_DAYS))}
                >
                  <span>Siguientes</span>
                  <ChevronRight size={16} />
                </Button>
              </>
            )}
            <Button
              type="button"
              variant="secondary"
              onClick={fs.toggle}
              title={fs.isFs ? 'Salir de pantalla completa' : 'Pantalla completa'}
            >
              {fs.isFs ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
            </Button>
          </div>
        </div>

        {useListLayout ? (
          // Periodos largos: lista vertical agrupada por día. Mantiene el
          // detalle por pedido sin sacrificar legibilidad cuando son 30+
          // días (mes o custom largo).
          <div
            className={cn(
              'space-y-3 overflow-y-auto pr-1',
              fs.isFs ? 'flex-1 min-h-0' : 'max-h-[720px]',
            )}
          >
            {days.map((d) => {
              const sales = salesByDate.get(ymd(d)) || []
              const isToday = sameDay(d, today)
              if (sales.length === 0) return null
              const totalStems = sales.reduce(
                (a, s) => a + (Number(s.totalStems) || 0),
                0,
              )
              return (
                <div
                  key={ymd(d)}
                  className={cn(
                    'rounded-2xl border bg-white',
                    isToday ? 'border-expo-primary' : 'border-gray-200',
                  )}
                >
                  <div
                    className={cn(
                      'px-4 py-2 rounded-t-2xl flex items-center justify-between',
                      isToday ? 'bg-expo-primary text-white' : 'bg-expo-dark text-white',
                    )}
                  >
                    <div className="font-semibold">
                      {DAY_NAMES[d.getDay()]} {d.getDate()}{' '}
                      {MONTH_NAMES_SHORT[d.getMonth()]}
                    </div>
                    <div className="text-xs opacity-80">
                      {sales.length} pedido{sales.length === 1 ? '' : 's'} ·{' '}
                      {totalStems.toLocaleString('es-CO')} tallos
                    </div>
                  </div>
                  <div className="p-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {sales.map((sale) => (
                      <SaleCard
                        key={sale.id}
                        sale={sale}
                        onOpen={onOpenSale}
                        clientsById={clientsById}
                      />
                    ))}
                  </div>
                </div>
              )
            })}
            {/* Si no hay nada que mostrar en TODO el rango */}
            {days.every((d) => (salesByDate.get(ymd(d)) || []).length === 0) && (
              <div className="text-sm text-gray-400 italic py-8 text-center">
                Sin pedidos programados en el rango seleccionado.
              </div>
            )}
          </div>
        ) : (
          <div
            className={cn('grid gap-4', gridColsClass, fs.isFs && 'flex-1 min-h-0')}
          >
            {days.map((d) => (
              <DayCard
                key={ymd(d)}
                date={d}
                sales={salesByDate.get(ymd(d)) || []}
                isToday={sameDay(d, today)}
                isWeekend={d.getDay() === 0 || d.getDay() === 6}
                onOpenSale={onOpenSale}
                fullscreen={fs.isFs}
                clientsById={clientsById}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * Agrupa el inventario por variedad con un sub-listado por stemLength.
 *   [{ variety, lengths:[{stemLength, stages:{unprocessed,in_process,cold_room}, total}], total }]
 */
function groupInventoryByVariety(rows) {
  const map = new Map()
  for (const r of rows || []) {
    const key = r.variety || '—'
    if (!map.has(key)) map.set(key, [])
    map.get(key).push({
      id: r.id,
      stemLength: Number(r.stemLength) || 0,
      stages: {
        unprocessed: Number(r.stages?.unprocessed?.total) || 0,
        in_process: Number(r.stages?.in_process?.total) || 0,
        cold_room: Number(r.stages?.cold_room?.total) || 0,
      },
    })
  }
  const out = []
  for (const [variety, arr] of map) {
    arr.sort((a, b) => a.stemLength - b.stemLength)
    const withTotals = arr.map((r) => ({
      ...r,
      total: r.stages.unprocessed + r.stages.in_process + r.stages.cold_room,
    }))
    const total = withTotals.reduce((a, r) => a + r.total, 0)
    out.push({ variety, lengths: withTotals, total })
  }
  out.sort((a, b) => b.total - a.total || a.variety.localeCompare(b.variety, 'es'))
  return out
}

function InventoryDetail({ inventory }) {
  const fs = useFullscreen()
  const groups = useMemo(() => groupInventoryByVariety(inventory), [inventory])

  const stageTotals = useMemo(() => {
    const t = { unprocessed: 0, in_process: 0, cold_room: 0 }
    for (const g of groups) {
      for (const r of g.lengths) {
        t.unprocessed += r.stages.unprocessed
        t.in_process += r.stages.in_process
        t.cold_room += r.stages.cold_room
      }
    }
    return t
  }, [groups])

  return (
    <div
      ref={fs.ref}
      className={cn(
        'rounded-2xl border border-gray-200 bg-white shadow-sm',
        fs.isFs && 'p-6 h-screen overflow-auto',
      )}
    >
      <div className="p-6">
        <div className="flex items-center gap-3 flex-wrap mb-4">
          <h3
            className={cn(
              'font-semibold text-expo-dark',
              fs.isFs ? 'text-2xl' : 'text-lg',
            )}
          >
            Inventario actual por variedad y longitud
          </h3>
          <div className="ml-auto flex items-center gap-2">
            {Object.entries(stageTotals).map(([stage, total]) => {
              const Icon = STAGE_ICON[stage]
              return (
                <span
                  key={stage}
                  className={cn(
                    'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium',
                    STAGE_COLOR[stage],
                  )}
                >
                  <Icon size={14} />
                  {STAGE_LABEL[stage]}: {total.toLocaleString('es-CO')}
                </span>
              )
            })}
            <Button
              type="button"
              variant="secondary"
              onClick={fs.toggle}
              title={fs.isFs ? 'Salir de pantalla completa' : 'Pantalla completa'}
            >
              {fs.isFs ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
            </Button>
          </div>
        </div>

        {groups.length === 0 && (
          <div className="text-sm text-gray-500 italic py-6 text-center">
            No hay inventario registrado todavía.
          </div>
        )}

        <div
          className={cn(
            'grid gap-4',
            fs.isFs
              ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
              : 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3',
          )}
        >
          {groups.map((g) => (
            <div
              key={g.variety}
              className="rounded-2xl border border-gray-100 bg-expo-light/30 p-4"
            >
              <div className="flex items-baseline justify-between mb-2">
                <div className="text-base font-bold text-expo-dark">
                  {g.variety}
                </div>
                <div className="text-xs text-gray-500">
                  total {g.total.toLocaleString('es-CO')} tallos
                </div>
              </div>
              <div className="overflow-hidden rounded-xl border border-gray-100 bg-white">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600 text-xs">
                    <tr>
                      <th className="text-left px-2 py-1.5 font-medium">Largo</th>
                      <th className="text-right px-2 py-1.5 font-medium">Sin proc.</th>
                      <th className="text-right px-2 py-1.5 font-medium">En proc.</th>
                      <th className="text-right px-2 py-1.5 font-medium">Cuarto frío</th>
                      <th className="text-right px-2 py-1.5 font-medium">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.lengths.map((row) => (
                      <tr key={row.id} className="border-t border-gray-100">
                        <td className="px-2 py-1.5 font-medium text-expo-dark">
                          {row.stemLength}cm
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          {row.stages.unprocessed.toLocaleString('es-CO')}
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          {row.stages.in_process.toLocaleString('es-CO')}
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          {row.stages.cold_room.toLocaleString('es-CO')}
                        </td>
                        <td className="px-2 py-1.5 text-right font-semibold">
                          {row.total.toLocaleString('es-CO')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/**
 * Selector de periodo del Dashboard (Hoy / Semana / Mes / Personalizado).
 *
 * El cliente pidió poder ver los KPIs en el rango que necesite. El custom
 * abre dos inputs date para from/to. Mantiene el estado en el padre.
 */
function PeriodFilter({ period, setPeriod, range, setRange }) {
  return (
    <div className="flex flex-wrap items-center gap-2 mb-4">
      <span className="text-sm text-gray-500">Periodo:</span>
      <div className="inline-flex rounded-xl border border-gray-200 bg-white overflow-hidden">
        {PERIOD_OPTIONS.map((o) => (
          <button
            key={o.id}
            type="button"
            onClick={() => setPeriod(o.id)}
            className={cn(
              'px-3 py-1.5 text-sm transition-colors',
              period === o.id
                ? 'bg-expo-primary text-white'
                : 'text-expo-dark hover:bg-expo-light/60',
            )}
          >
            {o.label}
          </button>
        ))}
      </div>
      {period === 'custom' && (
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={range.from || ''}
            onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
            className="rounded-lg border border-gray-200 px-2 py-1 text-sm"
          />
          <span className="text-gray-400 text-sm">→</span>
          <input
            type="date"
            value={range.to || ''}
            onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
            className="rounded-lg border border-gray-200 px-2 py-1 text-sm"
          />
        </div>
      )}
    </div>
  )
}

export default function DashboardPage() {
  const [period, setPeriod] = useState('week')
  const [range, setRange] = useState({ from: '', to: '' })

  // Construir el endpoint con query string solo cuando el rango personalizado
  // tiene ambas fechas; de lo contrario llamamos sin from/to.
  const statsEndpoint = useMemo(() => {
    const qs = new URLSearchParams({ period })
    if (period === 'custom') {
      if (!range.from || !range.to) return null
      qs.set('from', range.from)
      qs.set('to', range.to)
    }
    return `/stats?${qs.toString()}`
  }, [period, range.from, range.to])

  const { data, loading, error, refresh: refreshStats } = useApi(
    statsEndpoint,
    { enabled: Boolean(statsEndpoint) },
  )
  const salesApi = useApi('/sales')
  const inventoryApi = useApi('/inventory')
  const clientsApi = useApi('/clients')
  const [openSale, setOpenSale] = useState(null)

  // Mapa id→cliente para resolver la jerarquía mayorista↔final en tarjetas y
  // en el modal de detalle sin rebuscar en cada render.
  const clientsById = useMemo(() => {
    const m = new Map()
    for (const c of clientsApi.data || []) m.set(c.id, c)
    return m
  }, [clientsApi.data])

  /** Disparador único de refresco tras una transición manual de status. */
  const refreshAll = () => {
    refreshStats()
    salesApi.refresh()
    inventoryApi.refresh()
  }

  return (
    <div>
      <PageHeader
        title="Dashboard"
        subtitle="Resumen operativo de ventas, compras e inventario"
        icon={Calendar}
      />

      <PeriodFilter
        period={period}
        setPeriod={setPeriod}
        range={range}
        setRange={setRange}
      />

      {loading ? (
        <p className="text-gray-500">Cargando…</p>
      ) : error ? (
        <p className="text-red-600">Error: {String(error.message)}</p>
      ) : data ? (
        <>
          {/*
           * KPIs del dashboard.
           * Por petición del cliente NO se muestran valores en USD ni el
           * conteo de clientes (información interna). En su lugar se
           * muestran tallos vendidos y tallos comprados — filtrados por el
           * periodo seleccionado arriba.
           */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Kpi
              icon={Scissors}
              label="Tallos vendidos"
              value={Number(data.totals?.stemsSold || 0).toLocaleString('es-CO')}
              tone="primary"
            />
            <Kpi
              icon={ShoppingCart}
              label="Tallos comprados"
              value={Number(data.totals?.stemsBought || 0).toLocaleString('es-CO')}
              tone="olive"
            />
            <Kpi
              icon={Package}
              label="Ventas abiertas"
              value={data.counts?.openSales ?? 0}
              tone="dark"
            />
            <Kpi
              icon={Package}
              label="Compras abiertas"
              value={data.counts?.openPurchases ?? 0}
              tone="yellow"
            />
          </div>

          <UpcomingDispatches
            sales={salesApi.data || []}
            onOpenSale={setOpenSale}
            clientsById={clientsById}
            onStatusChanged={refreshAll}
            period={period}
            range={range}
          />

          <InventoryDetail inventory={inventoryApi.data || []} />

          <SaleDetailModal
            sale={openSale}
            onClose={() => setOpenSale(null)}
            clientsById={clientsById}
            onStatusChanged={() => {
              refreshAll()
              setOpenSale(null)
            }}
          />
        </>
      ) : null}
    </div>
  )
}
